import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ExternalLink, X } from 'lucide-react';
import type {
  RoutingConfigSkuSummaryTarget,
  RoutingConfigSummaryRecord,
  RoutingPolicyMode,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useChannelModelsList } from '../api/useChannelModels.ts';
import {
  useOrgRoutingAssignments,
  useRoutingConfigSkuSummary,
  useRoutingConfigs,
  useSetOrgRoutingAssignments,
} from '../api/useTenantRouting.ts';
import { StatusChip } from './StatusChip.tsx';
import { coerceErrorMessage, useToast } from './Toast.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  FormField,
  IconButton,
  Panel,
  Select,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';
import {
  policyModeLabelKey,
  routeConfigStatusLabelKey,
  routeConfigTypeLabelKey,
  sortSkuPoolTargets,
  isDeprecated,
} from '../pages/tenantRoutingShared.ts';

/** Gateway PUT /assignments rejects more than 8 ids (`INVALID_PAYLOAD`). */
const MAX_ORG_ROUTING_STACK = 8;

function sameIds(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

function moveId(ids: readonly string[], index: number, delta: number): readonly string[] {
  const target = index + delta;
  if (target < 0 || target >= ids.length) return ids;
  const next = [...ids];
  const current = next[index];
  const swapped = next[target];
  if (current === undefined || swapped === undefined) return ids;
  next[index] = swapped;
  next[target] = current;
  return next;
}

/**
 * Org routing workspace — switch-only governance (epic #852), simplified
 * to switcher + selected-config summary (epic #886 Design C).
 *
 * The org dimension exposes exactly one action: switching which active
 * routing config the org is assigned to. Routing content is maintained
 * on the tenant-routing config library; this surface renders a compact
 * read-only SKU summary of the selected config with a deep link into the
 * config detail sheet (`?config=<id>`).
 *
 * Admin-only since #1724 narrowed `/v1/ops/tenant-routing` to ops-admin and
 * #1785 restored this tab beside the customer-manager service-level tab. The
 * #1658 customer-manager projection is gone from ops-api, so every endpoint
 * this workspace reads answers 403 for any other role — the mount point in
 * Customer 360 is gated on `TENANT_ROUTING_ASSIGN_ROLES` — the same constant
 * behind that server gate — and nothing here renders a reduced view for a
 * session that could not load the data in the first place.
 */
export function TenantCustomRoutesWorkspace({
  orgId,
  testId = 'tenant-custom-routes',
}: {
  readonly orgId: string;
  readonly testId?: string;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const routingConfigs = useRoutingConfigs();
  const orgAssignments = useOrgRoutingAssignments(orgId);
  const setAssignments = useSetOrgRoutingAssignments();
  const [draftIds, setDraftIds] = useState<readonly string[] | null>(null);
  const [addNonce, setAddNonce] = useState(0);

  useEffect(() => {
    setDraftIds(null);
  }, [orgId]);

  const configsById = useMemo(() => {
    const map = new Map<string, RoutingConfigSummaryRecord>();
    for (const config of routingConfigs.data?.configs ?? []) {
      map.set(config.id, config);
    }
    return map;
  }, [routingConfigs.data?.configs]);

  const savedIds = useMemo(() => {
    const rows = [...(orgAssignments.data?.assignments ?? [])]
      .filter((row) => row.scope_type === 'org' && row.scope_id === orgId)
      .sort((left, right) => left.priority - right.priority);
    return rows.map((row) => row.config_id);
  }, [orgAssignments.data?.assignments, orgId]);

  const stackIds = draftIds ?? savedIds;
  const dirty = !sameIds(stackIds, savedIds);
  const inheritedConfig = (routingConfigs.data?.configs ?? []).find((config) => config.is_global_default) ?? null;
  const assigned = stackIds.length > 0;
  const topConfigId = stackIds[0];
  const summaryConfig = assigned
    ? (topConfigId === undefined ? null : configsById.get(topConfigId) ?? null)
    : inheritedConfig;

  const addableConfigs = useMemo(() => {
    const inStack = new Set(stackIds);
    return (routingConfigs.data?.configs ?? []).filter((config) =>
      config.status === 'active'
      && !isDeprecated(config)
      && !inStack.has(config.id)
    );
  }, [routingConfigs.data?.configs, stackIds]);

  const exclusiveNotLast = stackIds.some((id, index) => {
    const config = configsById.get(id);
    return config?.coverage_mode === 'exclusive' && index < stackIds.length - 1;
  });

  const loading = routingConfigs.isPending || orgAssignments.isPending;
  const saving = setAssignments.isPending;

  function handleSave(): void {
    setAssignments.mutate(
      { orgId, body: { config_ids: stackIds } },
      {
        onSuccess: () => {
          setDraftIds(null);
          toast.success(t('customer_manager_360.custom_routes.toast.stack_saved'));
        },
        onError: (error) => toast.error(
          error instanceof OpsApiError && error.code === 'ROUTE_CONFIG_DEPRECATED'
            ? t('tenant_routing.deprecation.error.blocked')
            : coerceErrorMessage(error),
        ),
      },
    );
  }

  return (
    <section data-testid={testId} className="flex flex-col gap-4">
      <Panel caption={t('customer_manager_360.custom_routes.custom_title')} padded>
        <div className="flex flex-col gap-4">
          <p className="m-0 text-dense text-paper-low">
            {t('customer_manager_360.custom_routes.switch_hint')}
          </p>
          {loading ? (
            <p className="m-0 text-body text-paper-low">{t('tenant_routing.loading')}</p>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 rounded-lg bg-well px-3 py-3">
                {stackIds.length === 0 ? null : (
                  <ol className="m-0 flex list-none flex-col gap-2 p-0" data-testid="customer-route-config-stack">
                    {stackIds.map((configId, index) => {
                      const config = configsById.get(configId);
                      const name = config?.name
                        ?? orgAssignments.data?.assignments.find((row) => row.config_id === configId)?.config_name
                        ?? configId;
                      const coverage = config?.coverage_mode;
                      return (
                        <li
                          key={configId}
                          className="flex flex-wrap items-center gap-2 rounded-lg bg-card px-3 py-2"
                          data-testid={`customer-route-config-stack-row-${configId}`}
                        >
                          <span className="font-mono text-micro text-paper-low">{index + 1}</span>
                          <span className="min-w-0 flex-1 font-medium text-paper">{name}</span>
                          <Chip
                            tone={coverage === 'inherit' ? 'signal' : 'neutral'}
                            lamp={false}
                          >
                            {t(coverage === 'inherit'
                              ? 'tenant_routing.coverage.short_inherit'
                              : coverage === 'exclusive'
                                ? 'tenant_routing.coverage.short_exclusive'
                                : 'tenant_routing.coverage.short_unknown')}
                          </Chip>
                          <span className="font-mono text-micro text-paper-low">
                            {t('customer_manager_360.custom_routes.config_users')}
                            {' '}
                            {config?.assigned_org_count ?? 0}
                          </span>
                          <Link
                            to={`/supply/tenant-routing?config=${encodeURIComponent(configId)}`}
                            className="inline-flex items-center gap-1 rounded-sm text-micro font-semibold text-signal hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
                            data-testid={`customer-route-config-row-view-${configId}`}
                          >
                            {t('customer_manager_360.custom_routes.summary.view_full')}
                            <ExternalLink aria-hidden="true" className="h-3 w-3" />
                          </Link>
                          <IconButton
                            size="sm"
                            label={t('customer_manager_360.custom_routes.move_up')}
                            disabled={saving || index === 0}
                            onClick={() => setDraftIds(moveId(stackIds, index, -1))}
                            data-testid={`customer-route-config-move-up-${configId}`}
                          >
                            <ArrowUp aria-hidden="true" className="h-3.5 w-3.5" />
                          </IconButton>
                          <IconButton
                            size="sm"
                            label={t('customer_manager_360.custom_routes.move_down')}
                            disabled={saving || index === stackIds.length - 1}
                            onClick={() => setDraftIds(moveId(stackIds, index, 1))}
                            data-testid={`customer-route-config-move-down-${configId}`}
                          >
                            <ArrowDown aria-hidden="true" className="h-3.5 w-3.5" />
                          </IconButton>
                          <IconButton
                            size="sm"
                            label={t('customer_manager_360.custom_routes.remove')}
                            disabled={saving}
                            onClick={() => setDraftIds(stackIds.filter((id) => id !== configId))}
                            data-testid={`customer-route-config-remove-${configId}`}
                          >
                            <X aria-hidden="true" className="h-3.5 w-3.5" />
                          </IconButton>
                        </li>
                      );
                    })}
                  </ol>
                )}
                {exclusiveNotLast ? (
                  <p
                    role="alert"
                    className="m-0 rounded-lg border border-warn-line bg-warn-dim px-3 py-2 text-dense text-warn"
                    data-testid="customer-route-config-exclusive-warning"
                  >
                    {t('customer_manager_360.custom_routes.exclusive_not_last')}
                  </p>
                ) : null}
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-end">
                  <FormField
                    label={t('customer_manager_360.custom_routes.add_config')}
                    fieldId="customer-route-config-add"
                  >
                    <Select
                      key={addNonce}
                      value={undefined}
                      onValueChange={(configId) => {
                        if (stackIds.includes(configId) || stackIds.length >= MAX_ORG_ROUTING_STACK) return;
                        setDraftIds([...stackIds, configId]);
                        setAddNonce((nonce) => nonce + 1);
                      }}
                      options={addableConfigs.map((config) => ({ value: config.id, label: config.name }))}
                      placeholder={t('customer_manager_360.custom_routes.add_placeholder')}
                      disabled={saving || addableConfigs.length === 0 || stackIds.length >= MAX_ORG_ROUTING_STACK}
                      ariaLabel={t('customer_manager_360.custom_routes.add_config')}
                      testId="customer-route-config-add"
                    />
                  </FormField>
                  <div className="flex flex-wrap items-center gap-2">
                    {dirty ? (
                      <Chip tone="warn" lamp={false} testId="customer-route-config-dirty">
                        {t('customer_manager_360.custom_routes.dirty')}
                      </Chip>
                    ) : null}
                    <Button
                      variant="primary"
                      disabled={!dirty || saving}
                      loading={saving}
                      onClick={handleSave}
                      data-testid="customer-route-config-save"
                    >
                      {t('customer_manager_360.custom_routes.save')}
                    </Button>
                  </div>
                </div>
                {!assigned && inheritedConfig !== null ? (
                  <p className="m-0 text-micro text-paper-low" data-testid="customer-route-config-inherited">
                    {t('customer_manager_360.custom_routes.inherited_config_line', {
                      name: inheritedConfig.name,
                    })}
                  </p>
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                  <StatusChip
                    variant={assigned ? 'ok' : 'neutral'}
                    label={assigned
                      ? t('customer_manager_360.custom_routes.status.assigned_config')
                      : t('customer_manager_360.custom_routes.following_global')}
                    testId="customer-route-config-status"
                  />
                  <Link
                    to="/supply/tenant-routing"
                    className="rounded-sm text-micro font-semibold text-signal hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
                    data-testid="customer-route-config-manage-link"
                  >
                    {t('customer_manager_360.custom_routes.manage_configs')}
                  </Link>
                </div>
              </div>
              {summaryConfig !== null ? (
                <RouteConfigSummaryCard
                  config={summaryConfig}
                  assigned={assigned}
                />
              ) : null}
            </div>
          )}
        </div>
      </Panel>
    </section>
  );
}

/**
 * Read-only summary of the selected routing config (epic #886 Design C).
 * Assigned orgs read the same SKU catalog + per-SKU target/policy endpoints
 * as the config detail sheet. The summary is SKU-only; orgs following the
 * global default get the config identity plus the deep link only.
 */
function RouteConfigSummaryCard({
  config,
  assigned,
}: {
  readonly config: RoutingConfigSummaryRecord;
  readonly assigned: boolean;
}): JSX.Element {
  const t = useT();
  return (
    <div
      className="rounded-lg border border-line-soft bg-well px-3 py-3"
      data-testid="customer-route-config-summary"
    >
      <span className="block text-label text-paper-low" data-testid="customer-route-config-summary-name">
        {t('customer_manager_360.custom_routes.summary.title_named', { name: config.name })}
      </span>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Chip tone="neutral" lamp={false}>{t(routeConfigTypeLabelKey(config.config_type))}</Chip>
          <Chip tone={config.status === 'active' ? 'ok' : 'neutral'}>
            {t(routeConfigStatusLabelKey(config.status))}
          </Chip>
          {config.is_global_default ? (
            <Chip tone="signal" lamp={false}>{t('tenant_routing.configs.global_default_badge')}</Chip>
          ) : null}
        </div>
        <Link
          to={`/supply/tenant-routing?config=${encodeURIComponent(config.id)}`}
          className="inline-flex items-center gap-1 rounded-sm text-micro font-semibold text-signal hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
          data-testid="customer-route-config-view-link"
        >
          {t('customer_manager_360.custom_routes.summary.view_full')}
          <ExternalLink aria-hidden="true" className="h-3 w-3" />
        </Link>
      </div>
      {!assigned ? (
        <p
          className="m-0 mt-3 rounded-lg bg-card px-3 py-2 text-dense text-paper-low"
          data-testid="customer-route-config-summary-following"
        >
          {t('customer_manager_360.custom_routes.summary.following_hint')}
        </p>
      ) : (
        <AssignedConfigRoutingSummary configId={config.id} />
      )}
    </div>
  );
}

function AssignedConfigRoutingSummary({
  configId,
}: {
  readonly configId: string;
}): JSX.Element {
  const t = useT();
  const catalog = useChannelModelsList();
  const summary = useRoutingConfigSkuSummary(configId);
  const catalogSkus = useMemo(
    () => [...(catalog.data ?? [])].sort((a, b) => a.sku_name.localeCompare(b.sku_name)),
    [catalog.data],
  );
  const targetsBySku = useMemo(() => {
    const grouped = new Map<string, RoutingConfigSkuSummaryTarget[]>();
    for (const target of summary.data?.targets ?? []) {
      const current = grouped.get(target.sku_id) ?? [];
      current.push(target);
      grouped.set(target.sku_id, current);
    }
    return grouped;
  }, [summary.data?.targets]);
  const policyModeBySku = useMemo(
    () => new Map((summary.data?.policies ?? []).map((policy) => [policy.sku_id, policy.policy_mode])),
    [summary.data?.policies],
  );
  const configuredSkus = useMemo(() => {
    const catalogBySku = new Map(catalogSkus.map((sku) => [sku.sku_id, sku]));
    return [...targetsBySku.entries()]
      .map(([skuId, targets]) => {
        const catalogSku = catalogBySku.get(skuId);
        const snapshot = targets[0];
        return {
          skuId,
          skuName: catalogSku?.sku_name ?? snapshot?.sku_name ?? skuId,
          model: catalogSku?.model ?? snapshot?.model ?? '',
          targets: sortSkuPoolTargets(targets),
        };
      })
      .sort((a, b) => a.skuName.localeCompare(b.skuName));
  }, [catalogSkus, targetsBySku]);

  if (catalog.isPending || summary.isPending) {
    return (
      <p
        role="status"
        className="m-0 mt-3 rounded-lg bg-card px-3 py-2 text-dense text-paper-low"
        data-testid="customer-route-config-summary-loading"
      >
        {t('customer_manager_360.custom_routes.summary.loading')}
      </p>
    );
  }

  if (catalog.isError || summary.isError) {
    return (
      <p
        role="alert"
        className="m-0 mt-3 rounded-lg border border-warn-line bg-warn-dim px-3 py-2 text-dense text-warn"
        data-testid="customer-route-config-summary-error"
      >
        {t('customer_manager_360.custom_routes.summary.load_error')}
      </p>
    );
  }

  const missingSkus = catalogSkus.filter(
    (sku) => !(targetsBySku.get(sku.sku_id) ?? []).some((target) => target.enabled),
  );
  const coveredCount = catalogSkus.length - missingSkus.length;
  const missingPreview = missingSkus.slice(0, 3);
  const missingRemainder = missingSkus.length - missingPreview.length;

  return (
    <div className="mt-3 flex flex-col gap-3">
      <div
        className="rounded-lg border border-line-soft bg-card px-3 py-3"
        data-testid="customer-route-config-sku-coverage"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <span className="block text-label text-paper-low">
              {t('customer_manager_360.custom_routes.summary.coverage')}
            </span>
            <span className="mt-1 block font-mono text-title text-paper">
              {t('customer_manager_360.custom_routes.summary.coverage_value', {
                covered: String(coveredCount),
                total: String(catalogSkus.length),
              })}
            </span>
          </div>
          <Chip tone={missingSkus.length === 0 ? 'ok' : 'warn'} lamp={missingSkus.length === 0}>
            {missingSkus.length === 0
              ? t('customer_manager_360.custom_routes.summary.coverage_complete')
              : t('customer_manager_360.custom_routes.summary.coverage_missing_count', {
                  count: String(missingSkus.length),
                })}
          </Chip>
        </div>
        {missingSkus.length > 0 ? (
          <div className="mt-3" data-testid="customer-route-config-sku-missing">
            <span className="block text-label text-paper-low">
              {t('customer_manager_360.custom_routes.summary.missing_skus')}
            </span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {missingPreview.map((sku) => (
                <Chip key={sku.sku_id} tone="neutral" lamp={false}>
                  <span className="font-mono">{sku.sku_name}</span>
                </Chip>
              ))}
              {missingRemainder > 0 ? (
                <Chip tone="neutral" lamp={false}>
                  {t('customer_manager_360.custom_routes.summary.missing_more', {
                    count: String(missingRemainder),
                  })}
                </Chip>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <section data-testid="customer-route-config-sku-routes">
        <div className="mb-2">
          <span className="block text-section text-paper">
            {t('customer_manager_360.custom_routes.summary.sku_routes')}
          </span>
          <span className="mt-0.5 block text-micro text-paper-low">
            {t('customer_manager_360.custom_routes.summary.sku_routes_hint')}
          </span>
        </div>
        {configuredSkus.length === 0 ? (
          <p
            className="m-0 rounded-lg bg-card px-3 py-2 text-dense text-paper-low"
            data-testid="customer-route-config-summary-empty"
          >
            {t('customer_manager_360.custom_routes.summary.empty_sku')}
          </p>
        ) : (
          <SkuRouteSummaryTable skus={configuredSkus} policyModeBySku={policyModeBySku} />
        )}
      </section>
    </div>
  );
}

interface ConfiguredSkuRoute {
  readonly skuId: string;
  readonly skuName: string;
  readonly model: string;
  readonly targets: readonly RoutingConfigSkuSummaryTarget[];
}

function SkuRouteSummaryTable({
  skus,
  policyModeBySku,
}: {
  readonly skus: readonly ConfiguredSkuRoute[];
  readonly policyModeBySku: ReadonlyMap<string, RoutingPolicyMode>;
}): JSX.Element {
  const t = useT();
  return (
    <Table testId="customer-route-config-sku-table">
      <THead>
        <Tr interactive={false}>
          <Th>{t('customer_manager_360.custom_routes.summary.column.sku')}</Th>
          <Th>{t('customer_manager_360.custom_routes.summary.column.upstream')}</Th>
          <Th>{t('customer_manager_360.custom_routes.summary.column.dispatch')}</Th>
          <Th>{t('tenant_routing.table.status')}</Th>
        </Tr>
      </THead>
      <TBody>
        {skus.flatMap((sku) => sku.targets.map((target, index) => (
          <Tr
            key={target.id}
            data-testid={index === 0
              ? `customer-route-summary-sku-${sku.skuId}`
              : `customer-route-summary-sku-target-row-${target.id}`}
          >
            {index === 0 ? (
              <Td rowSpan={sku.targets.length} className="align-top">
                <span className="block font-mono text-paper">{sku.skuName}</span>
                <span className="mt-0.5 block font-mono text-micro text-paper-low">{sku.model}</span>
                <span className="mt-1 block text-micro text-paper-low">
                  {t('tenant_routing.default_channels.policy_mode')} ·{' '}
                  {t(policyModeLabelKey(policyModeBySku.get(sku.skuId) ?? 'full_chain'))}
                </span>
              </Td>
            ) : null}
            <Td data-testid={`customer-route-summary-sku-target-${target.id}`}>
              <span className="block font-mono text-paper">{target.upstream_id}</span>
              <span className="mt-0.5 block font-mono text-micro text-paper-low">
                {target.account_id} · {target.key_bucket_id} · {target.upstream_channel_slug}
              </span>
            </Td>
            <Td>
              <span className="block font-mono text-paper">
                {t('tenant_routing.default_channels.weight')} {target.weight}
              </span>
              <span className="mt-0.5 block font-mono text-micro text-paper-low">
                {t('tenant_routing.pool.retry_order')} {' '}
                {target.retry_order === null
                  ? t('tenant_routing.pool.retry_order_auto')
                  : target.retry_order}
                {' · '}{t('customer_manager_360.custom_routes.summary.column.priority')} {target.priority}
              </span>
            </Td>
            <Td>
              <Chip tone={target.enabled ? 'ok' : 'neutral'}>
                {target.enabled
                  ? t('tenant_routing.status.target_enabled')
                  : t('tenant_routing.status.target_disabled')}
              </Chip>
            </Td>
          </Tr>
        )))}
      </TBody>
    </Table>
  );
}
